import React, { useMemo, useState, useEffect } from "react";
import { ListChecks, AlertTriangle, CheckCircle2, Clock, ChevronRight, ChevronDown, ChevronUp, X, Flame, Equal, ChevronsDown, User, Tag, Calendar, Check, Search, Plus } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useApiData, fetchApi } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { KartaSeznam, DlazdiceFiltr, PruhFiltru, Segment, FiltrRadek, EmptyState, SkeletonRows, ErrorNote, Modal, HlavickaOkna, Zapinatko } from "../../components/ui.jsx";
import KlientPicker from "../../components/KlientPicker.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
// Sjednocení: manažer používá STEJNÝ detail/tvorbu úkolu jako trenér.
import { TaskDetailModal } from "../trener/Ukoly.jsx";

// Manažer → Úkoly. Pořadí: souhrn → Blížící se deadline (týden/14 dní) → Po deadline (červeně)
// → Plnění per trenér (rozklik: kategorie default zavřené, po deadline nahoře, Hotové s filtrem).

const OPEN_STATUSES = ["naplanovane", "v_procesu", "ke_kontrole"];
// Normalizace jména (bez diakritiky, lowercase) pro porovnání pilot úkolu vs HR trenér.
const normName = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim();
// Manažerské pozice se NEpovažují za aktivní trenéry v přehledu (Head Coach, manažer).
// Michal Šulc = Head Coach Senior, proto se v přehledu trenérů nezobrazuje.
const isMgmtPozice = (p) => /head\s*coach|mana[zž]er|manager/i.test(String((p && p.pozice) || "") + " " + String((p && p.role) || ""));
const STATUS_META = {
  naplanovane: { label: "Naplánované", pill: "bg-slate-100 text-slate-700 border-slate-200" },
  v_procesu:   { label: "V procesu",   pill: "bg-brand-100 text-brand-700 border-brand-200" },
  ke_kontrole: { label: "Ke kontrole", pill: "bg-pink-100 text-pink-700 border-pink-200" },
  hotovo:      { label: "Hotové",      pill: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  archiv:      { label: "Archiv",      pill: "bg-slate-100 text-slate-400 border-slate-200" },
};
const PRIORITY_META = {
  High:   { label: "Vysoká",  pill: "bg-red-100 text-red-800 border-red-200",       icon: Flame },
  Medium: { label: "Střední", pill: "bg-amber-100 text-amber-800 border-amber-200", icon: Equal },
  Low:    { label: "Nízká",   pill: "bg-slate-100 text-slate-700 border-slate-200", icon: ChevronsDown },
};
const DEADLINE_TONE = {
  overdue:  "bg-red-50 text-red-800 border-red-200",
  today:    "bg-amber-50 text-amber-800 border-amber-200",
  tomorrow: "bg-blue-50 text-blue-800 border-blue-200",
  future:   "bg-slate-50 text-slate-600 border-slate-200",
};
const TASK_CATEGORIES = [
  { key: "prodlouzeni", label: "Prodloužení", test: (s) => /prodlouzeni/.test(s) },
  { key: "planovani", label: "Plánování", test: (s) => /planovani|tyden|mesic/.test(s) },
  { key: "dokument", label: "Dokumenty", test: (s) => /dokument/.test(s) },
  { key: "onboarding", label: "Onboarding aplikace", test: (s) => /onboarding/.test(s) },
];
function taskCategory(task) {
  const tags = String(task.tags || "").toLowerCase();
  return TASK_CATEGORIES.find((c) => c.test(tags)) || { key: "ostatni", label: "Ostatní" };
}
const CAT_ORDER = [...TASK_CATEGORIES.map((c) => c.key), "ostatni"];
const CAT_LABEL = { ...Object.fromEntries(TASK_CATEGORIES.map((c) => [c.key, c.label])), ostatni: "Ostatní" };
const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDaysIso(iso, days) {
  const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + days);
  return d.toISOString().substring(0, 10);
}
function deadlineLabel(iso, today) {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { text: iso, tone: "future" };
  if (iso < today) {
    const days = Math.round((new Date(today + "T00:00:00") - new Date(iso + "T00:00:00")) / 86400000);
    return { text: `${days} d po`, tone: "overdue" };
  }
  if (iso === today) return { text: "Dnes", tone: "today" };
  const tmrw = addDaysIso(today, 1);
  if (iso === tmrw) return { text: "Zítra", tone: "tomorrow" };
  return { text: `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}.`, tone: "future" };
}

function Pill({ children, className }) {
  return <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-lg border ${className}`}>{children}</span>;
}
function PriorityPill({ priority }) {
  const m = PRIORITY_META[priority]; if (!m) return null;
  const Icon = m.icon;
  return <Pill className={m.pill}><Icon className="w-3 h-3" />{m.label}</Pill>;
}
function DeadlinePill({ iso, today }) {
  const d = deadlineLabel(iso, today); if (!d) return null;
  return <Pill className={DEADLINE_TONE[d.tone]}><Calendar className="w-3 h-3" />{d.text}</Pill>;
}
// Kompaktní řádek uvnitř rozbaleného trenéra (jméno + priorita + deadline).
function TaskRow({ t, today, onClick }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 pl-9 hover:bg-plum-50 cursor-pointer" onClick={onClick}>
      <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">{t.nazev}</span>
      <PriorityPill priority={t.priority} />
      <DeadlinePill iso={t.deadline} today={today} />
    </div>
  );
}
// Plný řádek pro sekce nahoře (trenér + priorita + klient + deadline pill).
function TaskListItem({ t, today, onClick }) {
  return (
    <div className="flex items-start gap-3 px-4 sm:px-5 py-2.5 hover:bg-slate-50 cursor-pointer" onClick={onClick}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate">{t.nazev}</div>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          <Pill className="bg-plum-50 text-plum-700 border-plum-200"><User className="w-3 h-3" />{t.trener}</Pill>
          <PriorityPill priority={t.priority} />
          {t.klient_name && <Pill className="bg-sky-50 text-sky-700 border-sky-200"><Tag className="w-3 h-3" />{t.klient_name}</Pill>}
        </div>
      </div>
      <DeadlinePill iso={t.deadline} today={today} />
    </div>
  );
}

// Datum splnění vůči deadline (pro hotové úkoly) — "splněno X d před/po termínu".
function fmtCzShort(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${parseInt(m[3], 10)}.${parseInt(m[2], 10)}.` : "";
}
function DoneDeadlineInfo({ deadline, hotovoKdy }) {
  const base = "text-[11px] font-bold rounded-md px-2 py-1 whitespace-nowrap border inline-flex items-center gap-1 flex-shrink-0";
  if (!deadline || !hotovoKdy) {
    return <span className={`${base} text-emerald-700 bg-emerald-50 border-emerald-200`}><CheckCircle2 className="w-3 h-3" /> {hotovoKdy ? `Splněno ${fmtCzShort(hotovoKdy)}` : "Hotovo"}</span>;
  }
  const d = Math.round((new Date(deadline + "T00:00:00") - new Date(hotovoKdy + "T00:00:00")) / 86400000);
  let txt, tone;
  if (d > 0) { txt = `splněno ${d} d před term.`; tone = "text-emerald-700 bg-emerald-50 border-emerald-200"; }
  else if (d === 0) { txt = "splněno v termínu"; tone = "text-emerald-700 bg-emerald-50 border-emerald-200"; }
  else { txt = `splněno ${-d} d po term.`; tone = "text-amber-800 bg-amber-50 border-amber-200"; }
  return <span className={`${base} ${tone}`}><CheckCircle2 className="w-3 h-3" /> {txt}</span>;
}
// Řádek do flat seznamu klikacích dlaždic — navíc status badge + datum splnění u hotových.
function FlatTaskRow({ t, today, onClick }) {
  const sm = STATUS_META[t.status] || STATUS_META.naplanovane;
  const isDone = t.status === "hotovo";
  return (
    <div className="flex items-start gap-3 px-4 sm:px-5 py-2.5 hover:bg-slate-50 cursor-pointer" onClick={onClick}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate">{t.nazev}</div>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          <Pill className="bg-plum-50 text-plum-700 border-plum-200"><User className="w-3 h-3" />{t.trener}</Pill>
          <span className={`inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full border ${sm.pill}`}>{sm.label}</span>
          <PriorityPill priority={t.priority} />
          {t.klient_name && <Pill className="bg-sky-50 text-sky-700 border-sky-200"><Tag className="w-3 h-3" />{t.klient_name}</Pill>}
          {t.v_manazerske && <Pill className="bg-amber-50 text-amber-800 border-amber-200">v manažerské nástěnce</Pill>}
        </div>
      </div>
      {isDone ? <DoneDeadlineInfo deadline={t.deadline} hotovoKdy={t.hotovo_kdy} /> : <DeadlinePill iso={t.deadline} today={today} />}
    </div>
  );
}

// Stylovaný dropdown filtr (ladí s designem, ne nativní select).
// Zavření filtrovaného seznamu. Sedí v tmavé hlavičce karty, proto bílé na průhledném.
function AkceZpet({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Zpět na přehled"
      aria-label="Zpět na přehled"
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white transition active:scale-95 sm:h-9 sm:w-9 sm:hover:bg-white/20"
    >
      <X className="h-4 w-4" />
    </button>
  );
}

// Rozbalovací skupina uvnitř karty. 12.9.2026: dřív měla jen tenkou šedou
// šipku vlevo, na telefonu se přehlédla a nebylo poznat, že jde o rozbalovátko.
// Teď je šipka vpravo v rámečku, stejná řeč jako u rozbalovacích výběrů, řádek
// je vysoký 48 px a otevřený se obarví, ať je jasné, co je rozbalené.
function RozbalovaciSkupina({ nazev, pocet, otevreno, onToggle, children }) {
  return (
    <div className={`overflow-hidden rounded-xl border ${otevreno ? "border-brand-200" : "border-slate-200"}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={otevreno}
        className={`flex min-h-[48px] w-full items-center gap-2 px-3 text-left ${otevreno ? "bg-brand-light" : "bg-white"}`}
      >
        <span className={`min-w-0 flex-1 text-sm font-bold ${otevreno ? "text-brand-950" : "text-slate-800"}`}>{nazev}</span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${otevreno ? "bg-white text-brand-950" : "bg-slate-100 text-slate-600"}`}>{pocet}</span>
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border bg-white ${otevreno ? "border-brand-200 text-brand-deep" : "border-slate-200 text-slate-500"}`}>
          {otevreno ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {otevreno && <div className="divide-y divide-slate-100 bg-white">{children}</div>}
    </div>
  );
}

// Manažer: vybere trenéra → vytvoří prázdný úkol → otevře se STEJNÝ detail modal jako u trenéra,
// kde doplní popis, klienta, deadline, podúkoly, komentáře atd. (jednotné napříč aplikací).
// Nový úkol pro trenéry. 12.9.2026 (rozhodnutí U2): dřív to byla dvě pole,
// úkol se založil prázdný a všechno se doplňovalo až v detailu. Teď se vyplní
// naráz, ovládací prvky jsou stejné jako v detailu trenérského úkolu (priorita
// jako tři tlačítka, deadline jako datum, klient přes stejný picker).
// Kategorie se ukládá do tagu, podle kterého stránka úkoly seskupuje; ručně
// založený úkol tak nespadne do „Ostatní" jako dřív.
const NOVY_SLOUPCE = [
  { key: "naplanovane", label: "Naplánované" },
  { key: "v_procesu", label: "V procesu" },
  { key: "ke_kontrole", label: "Ke kontrole" },
];
const PRIORITY_PLNE = {
  High: "border-red-600 bg-red-600 text-white",
  Medium: "border-amber-500 bg-amber-500 text-white",
  Low: "border-slate-500 bg-slate-500 text-white",
};

function AddTaskPicker({ trenerOptions, onClose, onCreated }) {
  const [pilots, setPilots] = useState([]); // vybraní trenéři (jména)
  const [nazev, setNazev] = useState("");
  const [popis, setPopis] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [status, setStatus] = useState("v_procesu");
  const [deadline, setDeadline] = useState("");
  const [kategorie, setKategorie] = useState("");
  const [klient, setKlient] = useState(null); // { klient_name, notion_id, lokalita }
  const [klientOtevren, setKlientOtevren] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const allValues = useMemo(() => trenerOptions.map((t) => t.value), [trenerOptions]);
  const allSelected = pilots.length > 0 && pilots.length === allValues.length;
  const toggle = (val) => setPilots((p) => (p.includes(val) ? p.filter((x) => x !== val) : [...p, val]));
  const toggleAll = () => setPilots(allSelected ? [] : allValues);

  // Co ještě chybí, ať to člověk nemusí hádat (stejný způsob jako v Operations).
  const chybi = [];
  if (!nazev.trim()) chybi.push("název");
  if (pilots.length === 0) chybi.push("aspoň jednoho trenéra");
  const chybiVeta = chybi.length <= 1 ? chybi.join("") : `${chybi.slice(0, -1).join(", ")} a ${chybi[chybi.length - 1]}`;

  const create = async () => {
    if (chybi.length) { setErr(`Doplň ${chybiVeta}.`); return; }
    setSaving(true); setErr("");
    try {
      let lastId = null;
      // Jeden POST na trenéra, každý dostane vlastní úkol se stejným obsahem.
      for (const pilot of pilots) {
        const r = await fetchApi("/api/trener-ukoly", {
          method: "POST",
          body: JSON.stringify({
            action: "create",
            task: {
              pilot,
              nazev: nazev.trim(),
              popis: popis.trim(),
              status,
              priority,
              deadline: deadline || "",
              tags: kategorie || "",
              klient_id: klient?.notion_id || "",
              klient_name: klient?.klient_name || "",
              lokalita: klient?.lokalita || "",
              zdroj: "manager",
            },
          }),
        });
        lastId = r.id;
      }
      // Jeden trenér → otevři rovnou detail. Víc trenérů → jen zavři a obnov
      // (nelze otevřít víc detailů naráz).
      onCreated(pilots.length === 1 ? lastId : null);
    } catch (e) { setErr("Nepodařilo se vytvořit úkol."); setSaving(false); }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      plnaHlavicka
      header={
        <HlavickaOkna
          ikona={ListChecks}
          nazev="Nový úkol"
          popis="Rovnou celý, ať ho nemusíš dodělávat"
          onClose={onClose}
        />
      }
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary">Zrušit</button>
          <button onClick={create} disabled={saving || chybi.length > 0} className="btn btn-primary">
            <Check className="h-4 w-4" />
            {/* Krátký popisek schválně: „Vytvořit a otevřít" se na telefonu lámalo do dvou řádků. */}
            <span className="whitespace-nowrap">{saving ? "Vytvářím…" : pilots.length > 1 ? `Vytvořit ${pilots.length}` : "Vytvořit"}</span>
          </button>
        </>
      }
    >
      <div className="space-y-3.5">
        <div>
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">Název *</div>
          <input className="field w-full text-base font-semibold" value={nazev} autoFocus
            onChange={(e) => setNazev(e.target.value)} placeholder="Co má trenér udělat?" />
        </div>

        <div>
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">Popis</div>
          <textarea className="field min-h-[64px] w-full" value={popis}
            onChange={(e) => setPopis(e.target.value)} placeholder="Detaily, podklady, souvislosti…" />
        </div>

        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Priorita</div>
          <div className="grid grid-cols-3 gap-2">
            {["High", "Medium", "Low"].map((pk) => {
              const meta = PRIORITY_META[pk];
              const PriIkona = meta.icon;
              const aktivni = priority === pk;
              return (
                <button
                  key={pk}
                  type="button"
                  onClick={() => setPriority(pk)}
                  aria-pressed={aktivni}
                  className={`inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border-2 px-2 text-xs font-bold transition-colors ${
                    aktivni ? PRIORITY_PLNE[pk] : "border-slate-200 bg-white text-slate-500"
                  }`}
                >
                  <PriIkona className="h-3.5 w-3.5" /> {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">Sloupec</div>
            <select className="field w-full" value={status} onChange={(e) => setStatus(e.target.value)}>
              {NOVY_SLOUPCE.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">Deadline</div>
            <input type="date" className="field w-full" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </div>
          <div>
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">Kategorie</div>
            <select className="field w-full" value={kategorie} onChange={(e) => setKategorie(e.target.value)}>
              <option value="">Ostatní</option>
              {TASK_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">Klient</div>
            {klient ? (
              <div className="flex min-h-[44px] items-center gap-2 rounded-xl border border-slate-300 bg-white px-3">
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 demo-sensitive">{klient.klient_name}</span>
                <button type="button" onClick={() => setKlient(null)} aria-label="Odebrat klienta"
                  className="shrink-0 rounded-lg p-1 text-slate-400 sm:hover:text-rose-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setKlientOtevren(true)}
                className="flex min-h-[44px] w-full items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-left">
                <span className="min-w-0 flex-1 text-sm text-slate-400">Nepovinné</span>
                <Search className="h-4 w-4 shrink-0 text-slate-400" />
              </button>
            )}
          </div>
        </div>

        {klientOtevren && (
          <KlientPicker
            currentName={klient?.klient_name || ""}
            onPick={(c) => { setKlient(c); setKlientOtevren(false); }}
            onCancel={() => setKlientOtevren(false)}
          />
        )}

        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Komu *</span>
            {pilots.length > 0 && (
              <span className="rounded-full bg-brand-light px-2 py-0.5 text-[11px] font-bold text-brand-deep">
                {pilots.length === 1 ? "vybrán 1" : `vybráni ${pilots.length}`}
              </span>
            )}
          </div>
          <Zapinatko
            zapnuto={allSelected}
            onZmena={toggleAll}
            popisek={`Všichni trenéři (${allValues.length})`}
          />
          <div className="mt-2 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
            {trenerOptions.map((t) => {
              const vybran = pilots.includes(t.value);
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => toggle(t.value)}
                  aria-pressed={vybran}
                  className={`flex min-h-[48px] w-full items-center gap-2.5 px-3 text-left ${vybran ? "bg-brand-light" : "bg-white"}`}
                >
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${vybran ? "border-brand bg-brand text-white" : "border-slate-300"}`}>
                    {vybran && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <span className={`text-sm font-semibold ${vybran ? "text-brand-950" : "text-slate-800"}`}>{t.label}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-slate-500">
            {pilots.length > 1
              ? "Každý vybraný trenér dostane vlastní úkol se stejným obsahem."
              : "Po vytvoření se otevře detail úkolu, kde jdou doplnit podúkoly a komentáře."}
          </p>
        </div>

        {err && <ErrorNote>{err}</ErrorNote>}
        {!err && chybi.length > 0 && (
          <p className="text-xs font-semibold text-amber-700">Ještě doplň {chybiVeta}.</p>
        )}
      </div>
    </Modal>
  );
}

export default function ManagerUkoly() {
  const { data, loading, error, refetch } = useApiData("/api/trener-ukoly");
  // Autoritativní zdroj trenérů = HR Lidé (status aktivní, is_trener, bez manažerských pozic).
  const { data: hrData } = useApiData("/api/hr-list-people?filter=treneri&status=aktivni");
  const [expanded, setExpanded] = useState(null);
  // 5.9.2026 (fáze 4b): tabulka o šesti sloupcích se na telefon nevejde, jména
  // se lámou. Na telefonu je z ní seznam: jméno na vlastním řádku a pod ním
  // štítky s celým popiskem. Schváleno (varianta B).
  const uzka = useUzkaObrazovka();
  const [openCats, setOpenCats] = useState(() => new Set());
  const [openDone, setOpenDone] = useState(() => new Set());
  const [doneYear, setDoneYear] = useState("all");
  const [doneMonth, setDoneMonth] = useState("all");
  const [upcomingRange, setUpcomingRange] = useState(7); // 7 = tento týden, 14 = do 14 dní
  const [upTrener, setUpTrener] = useState("all");
  const [upLokalita, setUpLokalita] = useState("all");
  const [openUpCats, setOpenUpCats] = useState(() => new Set());
  const [detailId, setDetailId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // 7.8.2026: deep link ?open=<id> (stejne jako trenersky board) — aby fungoval
  // odkaz zkopirovany z detailu ukolu i pro manazera.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    setDetailId(openId);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const today = todayIso();
  // Práva pro sdílený detail modal (stejný jako u trenéra).
  const mgrRole = getCurrentUser()?.role || "";
  const mgrCanApprove = ["owner", "manager", "head_coach"].includes(mgrRole);
  const mgrCanDelete = ["owner", "manager"].includes(mgrRole);

  // Aktivní trenéři z HR (vyloučeni manažeři jako Michal Šulc a neaktivní jako Jan Reichrich).
  const activeTrainers = useMemo(() => {
    const people = (hrData && hrData.people) || [];
    return people
      .filter((p) => p.is_trener && p.active !== false && !isMgmtPozice(p))
      .map((p) => ({ jmeno: p.display_name, lokalita: p.lokalita || "" }));
  }, [hrData]);
  const activeNameSet = useMemo(() => new Set(activeTrainers.map((t) => normName(t.jmeno))), [activeTrainers]);
  const trainerLokalita = useMemo(() => {
    const m = new Map();
    activeTrainers.forEach((t) => m.set(normName(t.jmeno), t.lokalita || ""));
    return m;
  }, [activeTrainers]);

  const { perTrener, byTrener, totals, overdueList, activeDated, flat } = useMemo(() => {
    const tasks = (data && data.tasks) || [];
    const map = {};
    const grp = {};
    const dated = [];
    for (const t of tasks) {
      const pilot = (t.pilot || "(bez trenéra)").trim();
      // Zobraz jen aktivní trenéry z HR. Dokud se HR nenačte (prázdná množina), neořezávej.
      if (activeNameSet.size > 0 && !activeNameSet.has(normName(pilot))) continue;
      if (!map[pilot]) { map[pilot] = { trener: pilot, total: 0, open: 0, overdue: 0, hotovo: 0 }; grp[pilot] = []; }
      grp[pilot].push(t);
      const m = map[pilot];
      const st = String(t.status || "naplanovane");
      m.total++;
      const isDone = st === "hotovo";
      const isActive = !isDone && st !== "archiv";
      if (isDone) m.hotovo++;
      if (OPEN_STATUSES.includes(st)) m.open++;
      if (isActive && t.deadline) { const c = taskCategory(t); dated.push({ id: t.id, trener: pilot, nazev: t.nazev, deadline: t.deadline, priority: t.priority, klient_name: t.klient_name, lokalita: trainerLokalita.get(normName(pilot)) || t.lokalita || "", catKey: c.key }); }
      if (isActive && t.deadline && t.deadline < today) m.overdue++;
    }
    const rows = Object.values(map).sort((a, b) => b.overdue - a.overdue || b.open - a.open);
    const tot = rows.reduce((a, r) => ({ total: a.total + r.total, open: a.open + r.open, overdue: a.overdue + r.overdue, hotovo: a.hotovo + r.hotovo }), { total: 0, open: 0, overdue: 0, hotovo: 0 });
    const overdue = dated.filter((d) => d.deadline < today).sort((a, b) => (a.deadline < b.deadline ? -1 : 1));
    // Flat seznam všech úkolů (jen aktivní trenéři) pro klikací dlaždice — filtr dle stavu + třídění.
    const flatList = [];
    for (const [pilot, arr] of Object.entries(grp)) {
      const lok = trainerLokalita.get(normName(pilot)) || "";
      for (const t of arr) flatList.push({ id: t.id, nazev: t.nazev, trener: pilot, status: String(t.status || "naplanovane"), priority: t.priority, deadline: t.deadline || "", klient_name: t.klient_name, lokalita: t.lokalita || lok, hotovo_kdy: t.hotovo_kdy || "", catKey: taskCategory(t).key });
    }
    return { perTrener: rows, byTrener: grp, totals: tot, overdueList: overdue, activeDated: dated, flat: flatList };
  }, [data, today, activeNameSet, trainerLokalita]);

  // Klikací dlaždice: vybraný stav (null = normální přehled) + třídění flat seznamu.
  const [statusView, setStatusView] = useState(null); // null | total | open | overdue | hotovo
  const [statusSort, setStatusSort] = useState("deadline"); // deadline | trener
  const [statusCat, setStatusCat] = useState("all"); // filtr kategorie ve flat seznamu
  const [statusSearch, setStatusSearch] = useState(""); // textové hledání (název / trenér / klient)
  // Úkoly daného stavu (před filtrem kategorie/textu) — pro nabídku kategorií.
  const statusBase = useMemo(() => {
    if (!statusView || !flat) return [];
    return flat.filter((t) => {
      const st = t.status;
      const isDone = st === "hotovo";
      const isActive = !isDone && st !== "archiv";
      if (statusView === "total") return true;
      if (statusView === "hotovo") return isDone;
      if (statusView === "open") return OPEN_STATUSES.includes(st);
      if (statusView === "overdue") return isActive && t.deadline && t.deadline < today;
      return false;
    });
  }, [flat, statusView, today]);
  const statusCatOptions = useMemo(() => {
    const present = new Set(statusBase.map((t) => t.catKey));
    return CAT_ORDER.filter((k) => present.has(k));
  }, [statusBase]);
  const statusList = useMemo(() => {
    const qn = statusSearch.trim().toLowerCase();
    const sel = statusBase
      .filter((t) => statusCat === "all" || t.catKey === statusCat)
      .filter((t) => !qn || `${t.nazev} ${t.trener} ${t.klient_name || ""}`.toLowerCase().includes(qn));
    return [...sel].sort((a, b) => {
      if (statusSort === "trener") return (a.trener || "").localeCompare(b.trener || "", "cs") || ((a.deadline || "9999") < (b.deadline || "9999") ? -1 : 1);
      const ad = a.deadline || "9999-99-99", bd = b.deadline || "9999-99-99";
      return ad < bd ? -1 : ad > bd ? 1 : 0;
    });
  }, [statusBase, statusSort, statusCat, statusSearch]);
  const STATUS_VIEW_LABEL = { total: "Všechny úkoly", open: "Otevřené úkoly", overdue: "Úkoly po deadline", hotovo: "Hotové úkoly" };
  const closeStatusView = () => { setStatusView(null); setStatusCat("all"); setStatusSearch(""); };

  const upcoming = useMemo(() => {
    const end = addDaysIso(today, upcomingRange);
    let list = activeDated.filter((d) => d.deadline >= today && d.deadline <= end);
    if (upTrener !== "all") list = list.filter((d) => d.trener === upTrener);
    if (upLokalita !== "all") list = list.filter((d) => d.lokalita === upLokalita);
    return list.sort((a, b) => (a.deadline < b.deadline ? -1 : 1));
  }, [activeDated, today, upcomingRange, upTrener, upLokalita]);
  const upcomingByCat = useMemo(() => {
    const g = {}; upcoming.forEach((t) => { (g[t.catKey] = g[t.catKey] || []).push(t); }); return g;
  }, [upcoming]);
  // Filtr trenérů = aktivní trenéři z HR (i ti s 0 úkoly), abecedně.
  const trenerOptions = useMemo(
    () => [...activeTrainers].sort((a, b) => a.jmeno.localeCompare(b.jmeno, "cs")).map((t) => ({ value: t.jmeno, label: t.jmeno })),
    [activeTrainers]
  );
  // Lokality = unikátní lokality aktivních trenérů (nyní 2).
  const lokalitaOptions = useMemo(() => {
    const s = [...new Set(activeTrainers.map((t) => t.lokalita).filter(Boolean))].sort((a, b) => a.localeCompare(b, "cs"));
    return s.map((l) => ({ value: l, label: l }));
  }, [activeTrainers]);

  const toggleCat = (key) => setOpenCats((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleUpCat = (key) => setOpenUpCats((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleDone = (key) => setOpenDone((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });

  function renderTrenerDetail(trener) {
    const all = byTrener[trener] || [];
    const activeAll = all.filter((t) => t.status !== "hotovo" && t.status !== "archiv");
    const overdue = activeAll.filter((t) => t.deadline && t.deadline < today);
    const active = activeAll.filter((t) => !(t.deadline && t.deadline < today));
    const done = all.filter((t) => t.status === "hotovo");

    const cats = {};
    active.forEach((t) => { const c = taskCategory(t); (cats[c.key] = cats[c.key] || []).push(t); });

    const doneYears = [...new Set(done.map((t) => (t.deadline || "").slice(0, 4)).filter(Boolean))].sort().reverse();
    const doneFiltered = done.filter((t) => {
      if (doneYear === "all" && doneMonth === "all") return true;
      const dl = t.deadline || "";
      if (doneYear !== "all" && dl.slice(0, 4) !== doneYear) return false;
      if (doneMonth !== "all" && dl.slice(5, 7) !== String(doneMonth).padStart(2, "0")) return false;
      return true;
    });
    const doneKey = `${trener}:done`;
    const doneOpen = openDone.has(doneKey);

    return (
      <div className="space-y-2">
        {overdue.length > 0 && (
          <div className="bg-rose-50 border border-rose-300 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-rose-200">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <span className="text-sm font-bold text-rose-800">Po deadline</span>
              <span className="text-xs font-bold text-rose-700 bg-rose-100 rounded-full px-2 py-0.5">{overdue.length}</span>
            </div>
            <div className="divide-y divide-rose-100">
              {overdue.map((t) => <TaskRow key={t.id} t={t} today={today} onClick={() => setDetailId(t.id)} />)}
            </div>
          </div>
        )}
        {active.length === 0 && overdue.length === 0 && <div className="text-sm text-slate-400 px-1">Žádné aktivní úkoly.</div>}
        {CAT_ORDER.filter((k) => (cats[k] || []).length > 0).map((k) => {
          const list = cats[k];
          const ckey = `${trener}:cat:${k}`;
          const open = openCats.has(ckey);
          return (
            <div key={k} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <button type="button" onClick={() => toggleCat(ckey)} className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50">
                {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <span className="text-sm font-bold text-slate-700">{CAT_LABEL[k]}</span>
                <span className="ml-1 text-xs font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{list.length}</span>
              </button>
              {open && <div className="divide-y divide-slate-100">{list.map((t) => <TaskRow key={t.id} t={t} today={today} onClick={() => setDetailId(t.id)} />)}</div>}
            </div>
          );
        })}
        {done.length > 0 && (
          <div className="bg-emerald-50/40 border border-emerald-200 rounded-xl overflow-hidden">
            <button type="button" onClick={() => toggleDone(doneKey)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-emerald-50">
              {doneOpen ? <ChevronDown className="w-4 h-4 text-emerald-600" /> : <ChevronRight className="w-4 h-4 text-emerald-600" />}
              <span className="text-sm font-bold text-emerald-800">Hotové</span>
              <span className="text-xs font-bold text-emerald-600">{done.length}</span>
            </button>
            {doneOpen && (
              <div>
                <div className="flex items-center gap-2 px-3 py-2 border-t border-emerald-100 bg-white flex-wrap">
                  <span className="text-xs text-slate-500">Filtr dle deadlinu:</span>
                  <select value={doneYear} onChange={(e) => setDoneYear(e.target.value)} className="field w-auto">
                    <option value="all">Všechny roky</option>
                    {doneYears.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select value={doneMonth} onChange={(e) => setDoneMonth(e.target.value)} className="field w-auto">
                    <option value="all">Všechny měsíce</option>
                    {MONTHS.map((mn, i) => <option key={i} value={String(i + 1)}>{mn}</option>)}
                  </select>
                  {(doneYear !== "all" || doneMonth !== "all") && (
                    <button onClick={() => { setDoneYear("all"); setDoneMonth("all"); }} className="text-xs text-plum-600 hover:underline">Zrušit filtr</button>
                  )}
                  <span className="text-xs text-slate-400 ml-auto">{doneFiltered.length} z {done.length}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {doneFiltered.length === 0 && <div className="px-3 py-3 text-center text-xs text-slate-400">Žádné hotové úkoly v tomto období.</div>}
                  {doneFiltered.map((t) => <TaskRow key={t.id} t={t} today={today} onClick={() => setDetailId(t.id)} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 5.9.2026: gradientová hlavička jako na ostatních stránkách, akce je ikona. */}
      <GradientHeader
        title="Úkoly trenérů"
        icon={ListChecks}
        subtitle="Plnění úkolů napříč týmem. Klikni na trenéra pro jeho úkoly, na úkol pro detail."
        loading={loading}
        akce={[{ klic: "novy", ikona: Plus, popis: "Přidat úkol", onClick: () => setAddOpen(true) }]}
      />

      {addOpen && (
        <AddTaskPicker
          trenerOptions={trenerOptions}
          onClose={() => setAddOpen(false)}
          onCreated={(id) => { setAddOpen(false); refetch(); if (id) setDetailId(id); }}
        />
      )}

      {loading && <SkeletonRows rows={6} />}
      {error && <ErrorNote onRetry={refetch}>Nepodařilo se načíst úkoly.</ErrorNote>}

      {!loading && !error && (
        <>
          {/* 12.9.2026: dlaždice ze vzorníku. Barva je v proužku nahoře podle
              významu, zapnutá má fialový rám a napíše „filtr zapnutý". */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
            <DlazdiceFiltr popisek="Úkolů celkem" hodnota={totals.total} ikona={ListChecks} ton="znacka"
              zapnuto={statusView === "total"} onClick={() => setStatusView((v) => (v === "total" ? null : "total"))} />
            <DlazdiceFiltr popisek="Otevřené" hodnota={totals.open} ikona={Clock} ton="info"
              zapnuto={statusView === "open"} onClick={() => setStatusView((v) => (v === "open" ? null : "open"))} />
            <DlazdiceFiltr popisek="Po deadline" hodnota={totals.overdue} ikona={AlertTriangle} ton="problem"
              zapnuto={statusView === "overdue"} onClick={() => setStatusView((v) => (v === "overdue" ? null : "overdue"))} />
            <DlazdiceFiltr popisek="Hotové" hodnota={totals.hotovo} ikona={CheckCircle2} ton="ok"
              zapnuto={statusView === "hotovo"} onClick={() => setStatusView((v) => (v === "hotovo" ? null : "hotovo"))} />
          </div>

          {/* Klikací dlaždice → flat tříditelný seznam dané kategorie (vč. hotových). */}
          {statusView && (
            <>
              <PruhFiltru zapnuto={statusCat !== "all" || !!statusSearch.trim()}
                onZrusit={() => { setStatusCat("all"); setStatusSearch(""); }}>
                <Segment
                  aria-label="Řazení"
                  value={statusSort}
                  onChange={setStatusSort}
                  items={[{ key: "deadline", label: "Dle deadline" }, { key: "trener", label: "Dle trenéra" }]}
                />
                <FiltrRadek
                  stitek="Kategorie"
                  vice={false}
                  popisVse="Všechny kategorie"
                  value={statusCat === "all" ? "" : statusCat}
                  onChange={(v) => setStatusCat(v || "all")}
                  items={statusCatOptions.map((k) => ({ key: k, label: CAT_LABEL[k] }))}
                />
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={statusSearch}
                    onChange={(e) => setStatusSearch(e.target.value)}
                    placeholder="Hledat úkol, trenéra nebo klienta…"
                    className="field w-full pl-9 pr-3"
                  />
                </div>
              </PruhFiltru>

              <KartaSeznam
                ikona={ListChecks}
                title={STATUS_VIEW_LABEL[statusView]}
                odznak={statusList.length}
                akce={<AkceZpet onClick={closeStatusView} />}
              >
                <div className="divide-y divide-slate-100">
                  {statusList.length === 0 && (
                    <EmptyState icon={ListChecks} title="Nic neodpovídá filtru" hint="Zruš hledání nebo přepni kategorii." />
                  )}
                  {statusList.map((t) => <FlatTaskRow key={t.id} t={t} today={today} onClick={() => setDetailId(t.id)} />)}
                </div>
              </KartaSeznam>
            </>
          )}

          {/* Normální přehled (sekce) — skrytý, když je aktivní klikací dlaždice (statusView). */}
          {!statusView && (<>
          {/* Blížící se deadline. 12.9.2026: filtry byly namačkané v barevné
              hlavičce karty, každý jiný prvek. Teď jsou v proužku nad kartou
              ze vzorníku, stejně jako v HR. */}
          <PruhFiltru
            zapnuto={upTrener !== "all" || upLokalita !== "all"}
            onZrusit={() => { setUpTrener("all"); setUpLokalita("all"); }}
          >
            <Segment
              aria-label="Období"
              value={String(upcomingRange)}
              onChange={(v) => setUpcomingRange(Number(v))}
              items={[{ key: "0", label: "Dnes" }, { key: "7", label: "Týden" }, { key: "14", label: "14 dní" }]}
            />
            <FiltrRadek
              stitek="Trenér"
              vice={false}
              popisVse="Všichni trenéři"
              value={upTrener === "all" ? "" : upTrener}
              onChange={(v) => setUpTrener(v || "all")}
              items={trenerOptions.map((o) => ({ key: o.value, label: o.label }))}
            />
            <FiltrRadek
              stitek="Lokalita"
              vice={false}
              popisVse="Všechny lokality"
              value={upLokalita === "all" ? "" : upLokalita}
              onChange={(v) => setUpLokalita(v || "all")}
              items={lokalitaOptions.map((o) => ({ key: o.value, label: o.label }))}
            />
          </PruhFiltru>

          <KartaSeznam ikona={Calendar} title="Blížící se deadline" odznak={upcoming.length}>
            <div className="space-y-2 p-2">
              {upcoming.length === 0 && (
                <EmptyState icon={Calendar} title="Nic v tomto období" hint="Přepni období nebo zruš filtry nad kartou." />
              )}
              {CAT_ORDER.filter((k) => (upcomingByCat[k] || []).length > 0).map((k) => {
                const list = upcomingByCat[k];
                const open = openUpCats.has(k);
                return (
                  <RozbalovaciSkupina
                    key={k}
                    nazev={CAT_LABEL[k]}
                    pocet={list.length}
                    otevreno={open}
                    onToggle={() => toggleUpCat(k)}
                  >
                    {list.map((t) => <TaskListItem key={t.id} t={t} today={today} onClick={() => setDetailId(t.id)} />)}
                  </RozbalovaciSkupina>
                );
              })}
            </div>
          </KartaSeznam>

          {/* Po deadline. Naléhavost nese červený odznak v tmavé hlavičce,
              barevné plochy zmizely (rozhodnutí ze čtvrté kapitoly). */}
          <KartaSeznam ikona={AlertTriangle} title="Úkoly po deadline" odznak={overdueList.length}
            odznakTon={overdueList.length > 0 ? "cervena" : "bila"}>
            <div className="divide-y divide-slate-100">
              {overdueList.length === 0 && (
                <EmptyState icon={CheckCircle2} title="Nikdo nemá úkol po deadline" hint="Tým je v termínu." />
              )}
              {overdueList.map((t) => <TaskListItem key={t.id} t={t} today={today} onClick={() => setDetailId(t.id)} />)}
            </div>
          </KartaSeznam>

          {/* Plnění per trenér */}
          <KartaSeznam ikona={ListChecks} title="Plnění per trenér" odznak={perTrener.length || null}>
            {uzka ? (
              <div className="divide-y divide-slate-100">
                {perTrener.length === 0 && (
                  <div className="px-4 py-6 text-center text-slate-400">Žádné úkoly.</div>
                )}
                {perTrener.map((r) => {
                  const pct = r.total > 0 ? Math.round((r.hotovo / r.total) * 100) : 0;
                  const isOpen = expanded === r.trener;
                  const vseHotovo = r.open === 0 && r.overdue === 0;
                  return (
                    <div key={r.trener}>
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : r.trener)}
                        aria-expanded={isOpen}
                        className="w-full px-3 py-2.5 text-left"
                      >
                        {/* 12.9.2026: šipka vpravo v rámečku, ať je poznat, že se
                            řádek rozbaluje. Dřív byla vlevo, tenká a šedá. */}
                        <span className="flex items-center gap-2">
                          <span className="min-w-0 flex-1 text-sm font-bold text-slate-900">{r.trener}</span>
                          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border bg-white ${isOpen ? "border-brand-200 text-brand-deep" : "border-slate-200 text-slate-500"}`}>
                            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        </span>
                        <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                            <b className="tabular-nums text-slate-900">{r.total}</b> celkem
                          </span>
                          {r.open > 0 && (
                            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                              <b className="tabular-nums">{r.open}</b> {r.open === 1 ? "otevřený" : r.open < 5 ? "otevřené" : "otevřených"}
                            </span>
                          )}
                          {r.overdue > 0 && (
                            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                              <b className="tabular-nums">{r.overdue}</b> po termínu
                            </span>
                          )}
                          {vseHotovo && (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">vše hotovo</span>
                          )}
                          {!vseHotovo && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                              <b className="tabular-nums text-slate-900">{pct}</b> % hotovo
                            </span>
                          )}
                        </span>
                      </button>
                      {isOpen && <div className="bg-slate-50/50 px-3 pb-3">{renderTrenerDetail(r.trener)}</div>}
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-2 font-bold text-slate-700">Trenér</th>
                    <th className="text-right px-4 py-2 font-bold text-slate-700">Celkem</th>
                    <th className="text-right px-4 py-2 font-bold text-slate-700">Otevřené</th>
                    <th className="text-right px-4 py-2 font-bold text-slate-700">Po deadline</th>
                    <th className="text-right px-4 py-2 font-bold text-slate-700">Hotové</th>
                    <th className="text-right px-4 py-2 font-bold text-slate-700">% hotovo</th>
                  </tr>
                </thead>
                <tbody>
                  {perTrener.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">Žádné úkoly.</td></tr>
                  )}
                  {perTrener.map((r) => {
                    const pct = r.total > 0 ? Math.round((r.hotovo / r.total) * 100) : 0;
                    const isOpen = expanded === r.trener;
                    return (
                      <React.Fragment key={r.trener}>
                        <tr className="border-b border-slate-100 hover:bg-plum-50/40 cursor-pointer" onClick={() => setExpanded(isOpen ? null : r.trener)}>
                          <td className="px-4 py-3 font-semibold text-slate-800">
                            <span className="inline-flex items-center gap-1.5">
                              {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                              {r.trener}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">{r.total}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">{r.open}</td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {r.overdue > 0
                              ? <span className="font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2 py-0.5">{r.overdue}</span>
                              : <span className="text-slate-400">0</span>}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">{r.hotovo}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-700">{pct} %</td>
                        </tr>
                        {isOpen && (
                          <tr className="border-b border-slate-100 bg-slate-50/50">
                            <td colSpan={6} className="px-4 sm:px-6 py-3">{renderTrenerDetail(r.trener)}</td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </KartaSeznam>
          </>)}
        </>
      )}

      {detailId && (
        <TaskDetailModal
          taskId={detailId}
          userRole={mgrRole}
          canApprove={mgrCanApprove}
          canDelete={mgrCanDelete}
          trenerOptions={trenerOptions}
          onClose={() => { setDetailId(null); refetch(); }}
        />
      )}
    </div>
  );
}


